import { StepShareLimits, type FormData } from '../../../articles-of-assocs/components/steps/StepShareLimits';
import { useAoaUpdateMutation, useCreateArticlesOfAssocMutation } from '../../../../graphql/react-operations';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { captureException } from '@sentry/react';
import { Trans, useLingui } from '@lingui/react';
import { Alert } from '@startuptools/ui/joy/Alert';
import { Stack } from '@startuptools/ui/joy/Stack';
import { RequireMeeting } from '../../../../components/RequireMeeting';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import BigNumber from 'bignumber.js';
import { AuthorizationDocument } from '../../Authorization.graphql';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import {
  GqlArticlesOfAssoc,
  GqlAuthorizationGroup,
  GqlCompany,
  GqlMeeting,
  GqlMeetingConcern,
} from '../../../../graphql/base-types.graphql';

export const AgStepShareLimits = ({
  company,
  authorizationGroup,
}: {
  company: Pick<GqlCompany, 'id' | 'sharesMax' | 'sharesTotal'>;
  authorizationGroup: Pick<
    GqlAuthorizationGroup,
    'id' | 'companyId' | 'otherIncreasesOfShares' | 'maxNumberOfShares' | 'requiresShareLimitChange'
  > & {
    meetingConcern?:
      | (Pick<GqlMeetingConcern, 'id'> & {
          meeting: Pick<GqlMeeting, 'id' | 'kind'>;
        })
      | null;
    articlesOfAssoc?: Pick<
      GqlArticlesOfAssoc,
      | 'id'
      | 'changeNumberOfShares'
      | 'numberOfSharesBefore'
      | 'numberOfSharesAfter'
      | 'changeShareLimits'
      | 'minimumNumberOfShares'
      | 'maximumNumberOfShares'
      | 'minimumShareCapital'
      | 'maximumShareCapital'
      | 'quotaAfter'
    > | null;
  };
}) => {
  const { stepNext, stepTo } = useStepper();

  const [createAoa, { loading: createAoaLoading }] = useCreateArticlesOfAssocMutation({
    refetchQueries: [AuthorizationDocument],
    variables: {
      companyId: authorizationGroup.companyId,
      input: {
        meetingId: authorizationGroup.meetingConcern?.meeting.id,
      },
    },
    awaitRefetchQueries: true,
  });

  const [save, { loading }] = useAoaUpdateMutation({
    refetchQueries: [AuthorizationDocument],
    awaitRefetchQueries: true,
  });

  const aoa = authorizationGroup.articlesOfAssoc;

  const onSave = async (input: Partial<FormData>, stepNext?: () => Promise<unknown>) => {
    if (!aoa) {
      throw new Error('aoa is required');
    }
    try {
      await save({
        variables: {
          companyId: authorizationGroup.companyId,
          input: {
            ...input,
            id: aoa.id,
          },
        },
      });
      if (stepNext) {
        await stepNext();
      }
    } catch (error) {
      captureException(error);
      genericErrorToast();
    }
  };

  const { otherIncreasesOfShares } = authorizationGroup;

  const { i18n } = useLingui();

  const splitFactor = aoa
    ? new BigNumber(aoa.numberOfSharesAfter ?? aoa.numberOfSharesBefore).div(aoa.numberOfSharesBefore)
    : 1;

  const totalShares = new BigNumber(authorizationGroup.maxNumberOfShares ?? 0).plus(
    new BigNumber(company.sharesTotal ?? 0).times(splitFactor),
  );

  return (
    <Stack gap={2}>
      {authorizationGroup.requiresShareLimitChange ? (
        <Alert color="warning">
          <Stack gap={1}>
            <div>
              <Trans
                id="The proposed authorization will result in a maximum of {newShares} new shares, on top of the current registered shares in the company ({existingShares})."
                values={{
                  newShares: (
                    <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                      {authorizationGroup.maxNumberOfShares}
                    </NumberFormat>
                  ),
                  existingShares: (
                    <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                      {company.sharesTotal}
                    </NumberFormat>
                  ),
                }}
              />
              {new BigNumber(otherIncreasesOfShares).gt(0) && (
                <Trans
                  id="There are also additional proposals to issue {otherIncreasesOfShares} more shares. This will affect the total number of shares."
                  values={{ otherIncreasesOfShares }}
                />
              )}
            </div>
            <div>
              <Trans
                id="Since the current maximum number of shares is {sharesMax}, this authorization cannot be registered without changing the articles of association if fully subscribed. Therefore, the limits need to be set higher. However, be careful not to set the minimum number of shares too high - if the authorization is not fully subscribed, the new minimum must be lower than or equal to the actual number of shares after the authorization."
                values={{
                  sharesMax: (
                    <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                      {company.sharesMax}
                    </NumberFormat>
                  ),
                }}
              />
            </div>
          </Stack>
        </Alert>
      ) : (
        !authorizationGroup.articlesOfAssoc?.changeShareLimits && (
          <Alert color="success">
            <Trans id="The maximum number of shares in the authorization fits within the companies share limits, which means you don't need to change the share limits." />
          </Alert>
        )
      )}

      <RequireMeeting
        meetingKind={authorizationGroup.meetingConcern?.meeting.kind}
        navigateToMeetingStep={() => stepTo('decision')}
        skip={stepNext}
        requiresUpdateMessage={<></>}
        requiresCreateMessage={
          <Trans id="Changing share limits requires a decision from the shareholders' meeting. Connect a meeting to change the share limits." />
        }
      >
        <StepShareLimits
          loading={loading || createAoaLoading}
          stepNext={stepNext}
          numberOfSharesAfter={aoa?.numberOfSharesAfter ?? aoa?.numberOfSharesBefore ?? ''}
          changeShareLimits={aoa?.changeShareLimits ?? false}
          quotaValueAfter={aoa?.quotaAfter ?? ''}
          mustBeInRange={totalShares}
          minimumNumberOfShares={aoa?.minimumNumberOfShares ?? ''}
          maximumNumberOfShares={aoa?.maximumNumberOfShares ?? ''}
          minimumShareCapital={aoa?.minimumShareCapital ?? ''}
          maximumShareCapital={aoa?.maximumShareCapital ?? ''}
          key={aoa?.changeShareLimits ? 'changeShareLimits' : 'noChangeShareLimits'}
          onToggle={async toggle => {
            try {
              if (toggle) {
                if (!aoa) {
                  await createAoa();
                } else {
                  await onSave({ changeShareLimits: true });
                }
                return;
              } else {
                if (aoa) {
                  await onSave({ changeShareLimits: false });
                }
              }
            } catch (error) {
              captureException(error);
              genericErrorToast();
            }
          }}
          onSave={data => onSave(data, stepNext)}
        />
      </RequireMeeting>
    </Stack>
  );
};
